/**
 * Eine Setlist bearbeiten: Name, Songs hinzufuegen, per Ziehen umsortieren.
 */
import type { View } from '../app.ts';
import { navigate } from '../app.ts';
import type { Setlist } from '../types.ts';
import { filterSongs, getSetlist, listSongs, saveSetlist } from '../db/repo.ts';
import { songMetaLine } from './library.ts';
import { button, field, h, textArea, textInput } from '../ui/dom.ts';
import { makeReorderable, moveItem } from '../ui/reorder.ts';
import { toast } from '../ui/toast.ts';
import { exportSetlist } from '../features/pdf/dialog.ts';

const SAVE_DELAY = 500;

export async function renderSetlistDetail(params: Record<string, string>): Promise<View> {
  const id = params.id as string;
  const existing = await getSetlist(id);
  const songs = await listSongs(existing?.projectId);
  if (!existing) {
    return {
      title: 'Setlist nicht gefunden',
      back: '#/setlists',
      body: h('div', { class: 'empty', text: 'Diese Setlist wurde geloescht.' }),
    };
  }

  let setlist: Setlist = existing;
  let saveTimer = 0;
  let dirty = false;
  const byId = new Map(songs.map((s) => [s.id, s]));

  function touch(patch: Partial<Setlist>): void {
    setlist = { ...setlist, ...patch };
    dirty = true;
    clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => void flush(), SAVE_DELAY);
  }

  async function flush(): Promise<void> {
    if (!dirty) return;
    dirty = false;
    setlist = await saveSetlist(setlist);
  }

  /* ---------------------------------------------------- Songliste */

  const songList = h('ul', { class: 'list' });

  // Immer vorhanden, aber nur mit Songs benutzbar – so erscheint der Knopf
  // sofort, wenn der erste Song hinzugefuegt wurde.
  const stageBtn = button('▶ Bühnen-Modus', () =>
    void flush().then(() => navigate(`#/stage/setlist/${setlist.id}/0`)),
  );

  const pdfBtn = button('PDF', () => void flush().then(() => exportSetlist(setlist)), { title: 'Als PDF exportieren' });

  function paintSongs(): void {
    stageBtn.disabled = setlist.songIds.length === 0;
    pdfBtn.disabled = setlist.songIds.length === 0;
    songList.textContent = '';
    if (setlist.songIds.length === 0) {
      songList.append(
        h('li', { class: 'empty', text: 'Noch keine Songs in dieser Setlist.' }),
      );
      return;
    }
    setlist.songIds.forEach((songId, index) => {
      const song = byId.get(songId);
      songList.append(
        h(
          'li',
          { class: 'list-item' },
          // Griff zum Ziehen – nur hier greift die Umsortierung.
          h('span', { class: 'drag-handle', text: '⠿', attrs: { 'aria-hidden': 'true' } }),
          h(
            'button',
            {
              class: 'list-item__main',
              attrs: { type: 'button' },
              on: { click: () => navigate(`#/stage/setlist/${setlist.id}/${index}`) },
            },
            h('span', {
              class: 'list-item__title',
              text: `${index + 1}. ${song?.title || 'Gelöschter Song'}`,
            }),
            h('span', { class: 'list-item__meta', text: song ? songMetaLine(song) : '' }),
          ),
          h(
            'div',
            { class: 'list-item__actions' },
            button(
              '×',
              () => {
                touch({ songIds: setlist.songIds.filter((_, i) => i !== index) });
                paintSongs();
              },
              { class: 'btn btn--ghost btn--icon', title: 'Aus Setlist entfernen' },
            ),
          ),
        ),
      );
    });
  }

  const stopReorder = makeReorderable(songList, {
    onReorder: (from, to) => {
      touch({ songIds: moveItem(setlist.songIds, from, to) });
      paintSongs();
    },
  });

  paintSongs();

  /* ------------------------------------------- Dialog: Songs waehlen */

  function openPicker(): void {
    let query = '';
    const results = h('ul', { class: 'list' });

    function paintResults(): void {
      results.textContent = '';
      const visible = filterSongs(songs, query, 'title');
      if (visible.length === 0) {
        results.append(h('li', { class: 'empty', text: 'Keine Songs gefunden.' }));
        return;
      }
      for (const song of visible) {
        const inList = setlist.songIds.includes(song.id);
        results.append(
          h(
            'li',
            { class: 'list-item' },
            h(
              'button',
              {
                class: 'list-item__main',
                attrs: { type: 'button' },
                on: {
                  click: () => {
                    // Mehrfaches Hinzufuegen ist erlaubt (Zugabe, Medley).
                    touch({ songIds: [...setlist.songIds, song.id] });
                    paintSongs();
                    paintResults();
                    toast(`"${song.title || 'Ohne Titel'}" hinzugefügt`);
                  },
                },
              },
              h('span', { class: 'list-item__title', text: song.title || 'Ohne Titel' }),
              h('span', {
                class: 'list-item__meta',
                text: (inList ? 'bereits in der Setlist · ' : '') + songMetaLine(song),
              }),
            ),
          ),
        );
      }
    }

    const modal = h(
      'div',
      { class: 'modal', attrs: { role: 'dialog', 'aria-modal': 'true' } },
      h(
        'div',
        { class: 'modal__panel' },
        h(
          'div',
          { class: 'modal__head' },
          h('h2', { class: 'modal__title', text: 'Songs hinzufügen' }),
          button('Fertig', () => modal.remove(), { class: 'btn btn--primary' }),
        ),
        textInput({
          placeholder: 'Suchen …',
          type: 'search',
          onInput: (value) => {
            query = value;
            paintResults();
          },
        }),
        h('div', { class: 'modal__body', style: { marginTop: '12px' } }, results),
      ),
    );

    // Tippen auf den Hintergrund schliesst den Dialog.
    modal.addEventListener('click', (ev) => {
      if (ev.target === modal) modal.remove();
    });

    paintResults();
    document.body.append(modal);
  }

  /* ---------------------------------------------------------- Aufbau */

  const body = h(
    'div',
    {},
    h(
      'section',
      { class: 'card' },
      field(
        'Name',
        textInput({
          value: setlist.name,
          placeholder: 'z. B. Trio, Jazzclub 12.10.',
          onInput: (value) => touch({ name: value }),
        }),
      ),
      field(
        'Notizen',
        textArea({
          value: setlist.notes,
          rows: 2,
          placeholder: 'Besetzung, Pausen, Zugaben …',
          onInput: (value) => touch({ notes: value }),
        }),
      ),
      h(
        'div',
        { class: 'btn-row' },
        button('+ Songs hinzufügen', openPicker, { class: 'btn btn--primary' }),
        stageBtn,
        pdfBtn,
      ),
    ),
    h(
      'section',
      { class: 'card' },
      h('h2', { class: 'card__title', text: 'Reihenfolge' }),
      h('p', { class: 'hint', text: 'Am Griff ⠿ ziehen, um die Reihenfolge zu ändern.' }),
      songList,
    ),
  );

  return {
    title: setlist.name || 'Setlist',
    back: '#/setlists',
    body,
    cleanup: () => {
      clearTimeout(saveTimer);
      stopReorder();
      void flush();
    },
  };
}
